/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, Package, Building2, ArrowRight, Loader2, Sparkles,
  ChevronRight, ShoppingBag, Tag, Award, X
} from 'lucide-react';
import { getBrands } from '../api';
import { getFullImageUrl } from '../utils/imageUtils';

interface Brand {
  _id: string;
  id?: number;
  name: string;
  slug: string;
  logo?: string;
  productCount?: number;
  description?: string;
  website?: string;
}

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45 }
  }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

const slideInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

/* ============================================================
   HELPER FUNCTIONS
   ============================================================ */
const getBrandImage = (brand: Brand) => {
  if (brand.logo) {
    return getFullImageUrl(brand.logo);
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(brand.name)}&size=200&background=0f172a&color=f8fafc&font-size=0.5&bold=true`;
};

/* ============================================================
   SKELETON COMPONENTS
   ============================================================ */
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
    <div className="w-16 h-16 rounded-xl bg-gray-200 mx-auto mb-3" />
    <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2" />
    <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto" />
  </div>
);

const SkeletonFeatured = () => (
  <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 p-6 animate-pulse">
    <div className="w-24 h-24 rounded-full bg-gray-200 mx-auto mb-4" />
    <div className="h-5 bg-gray-200 rounded w-2/3 mx-auto mb-2" />
    <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto" />
  </div>
);

/* ============================================================
   HERO SECTION
   ============================================================ */
const HeroSection: React.FC<{
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  totalBrands: number;
  totalProducts: number;
}> = ({ searchQuery, setSearchQuery, totalBrands, totalProducts }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <section className="relative overflow-hidden bg-slate-900">
      {/* Animated Background Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 60, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-600/30 to-purple-600/20 blur-3xl"
        />
        <motion.div
          animate={{ x: [0, -40, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-tl from-emerald-500/20 to-cyan-500/20 blur-3xl"
        />
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute top-20 right-1/4 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-amber-500/15 to-orange-500/15 blur-3xl"
        />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16 md:py-24 lg:py-32">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-6"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 text-sm text-white/90">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>Premium Brands Collection</span>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-5 leading-tight tracking-tight"
          >
            Discover{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              Top Brands
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center text-white/60 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Explore our curated collection of world-class brands. From electronics to fashion, find everything you love.
          </motion.p>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-2xl mx-auto mb-12"
          >
            <div className={`relative transition-all duration-300 ${isFocused ? 'transform scale-[1.02]' : ''}`}>
              <div className={`absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-0 transition-opacity duration-300 ${isFocused ? 'opacity-30' : ''}`} />
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl flex items-center overflow-hidden">
                <Search className="w-5 h-5 text-white/50 ml-5 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search brands like Apple, Samsung, Sony..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className="flex-1 bg-transparent border-none outline-none px-4 py-4 text-white placeholder-white/40 text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mr-3 p-1 rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap justify-center gap-4 md:gap-8"
          >
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{totalBrands}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider">Brands</div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{totalProducts.toLocaleString()}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider">Products</div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">100%</div>
                <div className="text-xs text-white/50 uppercase tracking-wider">Authentic</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 to-transparent" />
    </section>
  );
};

/* ============================================================
   FEATURED BRANDS SECTION
   ============================================================ */
const FeaturedBrands: React.FC<{ brands: Brand[] }> = ({ brands }) => {
  const featured = useMemo(() => {
    const sorted = [...brands].sort((a, b) => (b.productCount || 0) - (a.productCount || 0));
    return sorted.slice(0, Math.min(6, sorted.length));
  }, [brands]);

  if (featured.length === 0) return null;

  return (
    <section className="py-12 md:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={slideInLeft}
          className="flex items-center gap-3 mb-8"
        >
          <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full" />
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Top Brands</h2>
          <span className="ml-2 text-sm text-gray-400 hidden sm:inline">Most Popular</span>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((brand, i) => (
            <motion.div
              key={brand._id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Link
                to={`/brands/${brand.slug}`}
                className="group relative flex items-center gap-5 bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-xl hover:shadow-slate-200/50 hover:border-blue-200 transition-all duration-300 overflow-hidden"
              >
                {/* Hover Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50/0 via-blue-50/0 to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Brand Logo */}
                <div className="relative flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform duration-300">
                  <img
                    src={getBrandImage(brand)}
                    alt={brand.name}
                    className="w-full h-full object-contain p-3"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(brand.name)}&size=200&background=0f172a&color=f8fafc&font-size=0.5&bold=true`;
                    }}
                  />
                </div>

                {/* Brand Info */}
                <div className="flex-1 min-w-0 relative">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                    {brand.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                    <Package className="w-3.5 h-3.5 text-gray-400" />
                    <span>{brand.productCount || 0} Products</span>
                  </p>
                  {brand.description && (
                    <p className="text-xs text-gray-400 mt-2 line-clamp-2">{brand.description}</p>
                  )}
                  <div className="mt-3 flex items-center gap-1 text-blue-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span>Explore</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   BRAND CARD
   ============================================================ */
const BrandCard: React.FC<{ brand: Brand }> = ({ brand }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-30px' }}
      variants={fadeInUp}
    >
      <Link
        to={`/brands/${brand.slug}`}
        className="group relative flex flex-col items-center bg-white rounded-2xl border border-gray-100 p-5 md:p-6 hover:shadow-xl hover:shadow-slate-200/50 hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
      >
        {/* Subtle gradient on hover */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/0 to-blue-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Logo */}
        <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 overflow-hidden shadow-inner">
          <img
            src={getBrandImage(brand)}
            alt={brand.name}
            className="w-full h-full object-contain p-3 md:p-4"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(brand.name)}&size=200&background=0f172a&color=f8fafc&font-size=0.5&bold=true`;
            }}
          />
        </div>

        {/* Name */}
        <h3 className="relative text-center font-bold text-gray-900 group-hover:text-blue-600 transition-colors text-sm md:text-base mb-2 truncate max-w-full">
          {brand.name}
        </h3>

        {/* Product Count Pill */}
        <div className="relative inline-flex items-center gap-1.5 bg-gray-50 group-hover:bg-blue-50 rounded-full px-3 py-1.5 text-xs font-medium text-gray-500 group-hover:text-blue-600 transition-colors">
          <Tag className="w-3 h-3" />
          <span>{brand.productCount || 0} Products</span>
        </div>

        {/* Hover Arrow */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
            <ArrowRight className="w-4 h-4 text-white" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ============================================================
   EMPTY STATE
   ============================================================ */
const EmptyState: React.FC<{ searchQuery: string; onClear: () => void }> = ({ searchQuery, onClear }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
    className="flex flex-col items-center justify-center py-24 text-center"
  >
    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-gray-200 flex items-center justify-center mb-6">
      <Search className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-2">No brands found</h3>
    <p className="text-gray-500 mb-6 max-w-md">
      We couldn't find any brands matching &quot;<span className="font-semibold text-gray-700">{searchQuery}</span>&quot;. Try a different search term.
    </p>
    <button
      onClick={onClear}
      className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
    >
      <X className="w-4 h-4" />
      Clear Search
    </button>
  </motion.div>
);

/* ============================================================
   LOADING STATE
   ============================================================ */
const LoadingState = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Hero Skeleton */}
    <div className="bg-slate-900 py-20 md:py-28">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto animate-pulse">
          <div className="h-6 bg-white/10 rounded-full w-48 mx-auto mb-6" />
          <div className="h-14 bg-white/10 rounded-xl w-full mx-auto mb-4" />
          <div className="h-6 bg-white/10 rounded-xl w-2/3 mx-auto mb-10" />
          <div className="h-14 bg-white/10 rounded-2xl w-full mx-auto" />
        </div>
      </div>
    </div>

    {/* Featured Skeleton */}
    <div className="container mx-auto px-4 py-12">
      <div className="h-8 bg-gray-200 rounded-lg w-48 mb-8" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonFeatured key={i} />
        ))}
      </div>
    </div>

    {/* Grid Skeleton */}
    <div className="container mx-auto px-4 pb-16">
      <div className="h-8 bg-gray-200 rounded-lg w-32 mb-8" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
   ERROR STATE
   ============================================================ */
const ErrorState: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center max-w-md"
    >
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center mx-auto mb-6 border border-red-100">
        <Building2 className="w-10 h-10 text-red-400" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Failed to load brands</h2>
      <p className="text-gray-500 mb-8">{error}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
      >
        <Loader2 className="w-4 h-4" />
        Try Again
      </button>
    </motion.div>
  </div>
);

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const Brands: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<Brand[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    filterBrands();
  }, [brands, searchQuery]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBrands();

      let brandData: Brand[] = [];
      if (Array.isArray(response)) {
        brandData = response;
      } else if (response.data && Array.isArray(response.data)) {
        brandData = response.data;
      } else if (response.data?.brands && Array.isArray(response.data.brands)) {
        brandData = response.data.brands;
      } else if (response.brands && Array.isArray(response.brands)) {
        brandData = response.brands;
      }

      setBrands(brandData);
      setFilteredBrands(brandData);
    } catch (err: any) {
      console.error('Error fetching brands:', err);
      setError(err.message || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const filterBrands = React.useCallback(() => {
    let result = [...brands];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(brand =>
        brand.name.toLowerCase().includes(query) ||
        (brand.description && brand.description.toLowerCase().includes(query))
      );
    }

    setFilteredBrands(result);
  }, [brands, searchQuery]);

  const totalProducts = useMemo(
    () => brands.reduce((sum, b) => sum + (b.productCount || 0), 0),
    [brands]
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={fetchBrands} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <HeroSection
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        totalBrands={brands.length}
        totalProducts={totalProducts}
      />

      {/* Content */}
      {filteredBrands.length === 0 ? (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <EmptyState searchQuery={searchQuery} onClear={() => setSearchQuery('')} />
        </div>
      ) : (
        <>
          {/* Featured Brands */}
          {!searchQuery && <FeaturedBrands brands={filteredBrands} />}
        </>
      )}

      {/* Bottom CTA */}
      {!searchQuery && brands.length > 0 && (
        <section className="py-12 md:py-16 border-t border-gray-200/80">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={scaleIn}
              className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 text-center"
            >
              {/* Decorative dots */}
              <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
                <div className="absolute top-4 right-4 w-2 h-2 bg-white rounded-full" />
                <div className="absolute top-12 right-16 w-1.5 h-1.5 bg-white rounded-full" />
                <div className="absolute top-24 right-8 w-1 h-1 bg-white rounded-full" />
                <div className="absolute bottom-16 right-24 w-2 h-2 bg-white rounded-full" />
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Can&apos;t find your favorite brand?
              </h3>
              <p className="text-white/60 max-w-xl mx-auto mb-8 text-base md:text-lg">
                Explore our complete product catalog or get in touch for special brand requests.
              </p>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-2xl font-bold hover:bg-gray-100 transition-colors shadow-xl shadow-black/20 text-base"
              >
                <ShoppingBag className="w-5 h-5" />
                Browse All Products
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Brands;
